import { useEffect, useMemo, useState } from 'react';
import { ApiError, iptv, type SportEvent } from '../api';
import { ChannelLogo, playLink } from '../components/LiveBits';
import { ChannelSheet, HeroCard, HubCardView, PreviewWindow } from '../components/SportHub';
import { Crest } from '../components/SportCenterBits';
import { SportCenterSettings } from '../components/SportCenterSettings';
import { useIsOwner } from '../owner';
import { linkProps } from '../router';
import { dayRange, dayText, hhmm, usePolling } from '../sportcenter';
import { hubApi, setHideScores, useHideScores, type HubCard, type HubChannel, type HubFollow, type HubVideo } from '../sporthub';

const errText = (e: unknown, fb: string) => (e instanceof ApiError && e.message !== e.code ? e.message : fb);

// ---------- Voor jou ----------

export function ForYou() {
  const [from, to] = dayRange(0);
  const { data, error, reload } = usePolling(`hub:${from}`, () => hubApi.home(from, to));
  const hide = useHideScores();
  const [sheet, setSheet] = useState<HubCard | null>(null);
  const [win, setWin] = useState<{ ch: HubChannel; title: string } | null>(null);
  useEffect(() => {
    if (data) setHideScores(data.hideScores);
  }, [data]);

  if (error && !data) return <p className="placeholder-row error">{error}</p>;
  if (!data) return <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />;
  const open = (c: HubCard) => setSheet(c);
  const toWindow = (c: HubCard, ch?: HubChannel) => {
    const pick = ch ?? c.channels[0];
    if (pick) setWin({ ch: pick, title: c.title });
  };
  return (
    <div className={`sh-page${hide ? ' hide-scores' : ''}`}>
      <div className="sh-top">
        {data.hero ? <HeroCard c={data.hero} hide={hide} onOpen={open} onWindow={(c) => toWindow(c)} /> : (
          <section className="sh-hero sh-hero-empty">
            <b>Nu niets live van wat je volgt</b>
            <span className="subtle">Volg clubs, competities, sporten of personen: dan staat hun volgende wedstrijd hier.</span>
            <a className="btn btn-primary" {...linkProps('/sportcenter/volgen')}>Volgen</a>
          </section>
        )}
        <aside className="sh-side">
          {win ? <PreviewWindow ch={win.ch} title={win.title} onClose={() => setWin(null)} /> : (
            <div className="sh-side-hint">
              <b>Kijk mee in een venster</b>
              <span className="subtle">Kies bij een wedstrijd "Kijk in het venster": je blijft rondkijken terwijl hij speelt.</span>
            </div>
          )}
          {!data.iptv && <p className="subtle">Geen IPTV-account: je ziet uitslagen en schema's, maar geen zenders.</p>}
        </aside>
      </div>

      {data.live.length > 0 && (
        <section className="row" aria-label="Live en straks">
          <h2 className="sh-row-title"><span className="sh-dot" aria-hidden="true" />Live en straks <span className="subtle">Wat jij volgt eerst, daarna vandaag op jouw zenders</span></h2>
          <div className="row-track" data-focus-group>{data.live.map((c) => <HubCardView key={c.key} c={c} hide={hide} onOpen={open} />)}</div>
        </section>
      )}

      {data.teams.map((t) => (
        <section key={`${t.team.path}|${t.team.id}`} className="row" aria-label={t.team.name}>
          <h2 className="sh-row-title">
            <Crest logo={t.team.logo} name={t.team.short} size={30} />{t.team.short}
            {t.form && <span className="sh-form" aria-label={`Vorm: ${t.form}`}>{[...t.form].map((r, i) => <i key={i} className={`sh-f-${r}`}>{r}</i>)}</span>}
          </h2>
          {t.matches.length ? <div className="row-track" data-focus-group>{t.matches.map((c) => <HubCardView key={c.key} c={c} hide={hide} onOpen={open} />)}</div> : <p className="placeholder-row">Geen wedstrijden gepland.</p>}
        </section>
      ))}

      {data.weekend.length > 0 && (
        <section className="row" aria-label="Deze week">
          <h2 className="sh-row-title">Deze week <span className="subtle">F1, darts, tennis, UFC en wat je verder volgt, op jouw zenders</span></h2>
          <div className="row-track" data-focus-group>{data.weekend.map((c) => <HubCardView key={c.key} c={c} hide={hide} onOpen={open} />)}</div>
        </section>
      )}

      {sheet && <ChannelSheet c={sheet} onClose={() => { setSheet(null); reload(); }} onWindow={(c, ch) => toWindow(c, ch)} />}
    </div>
  );
}

// ---------- Zenders: sport die nu en straks op je zenders is ----------

export function SportChannels() {
  const [events, setEvents] = useState<SportEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => iptv.sport(8).then((r) => alive && setEvents(r.events)).catch((e) => alive && setError(errText(e, 'Zenders laden mislukt.')));
    void load();
    const t = window.setInterval(load, 60_000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);
  const channels = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, { ch: SportEvent['channels'][number]; now: SportEvent | null; next: SportEvent | null }>();
    for (const e of events ?? []) {
      if (e.rerun) continue;
      for (const ch of e.channels) {
        const k = `${ch.accountId}:${ch.streamId}`;
        const cur = map.get(k) ?? { ch, now: null, next: null };
        if (e.start <= now && e.stop > now) cur.now = cur.now ?? e;
        else if (e.start > now && (!cur.next || e.start < cur.next.start)) cur.next = e;
        map.set(k, cur);
      }
    }
    return [...map.values()].sort((a, b) => Number(!!b.now) - Number(!!a.now) || Number(b.now?.favorite ?? 0) - Number(a.now?.favorite ?? 0) || a.ch.name.localeCompare(b.ch.name)).slice(0, 60);
  }, [events]);
  if (error && !events) return <p className="placeholder-row error">{error}</p>;
  if (!events) return <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />;
  if (!channels.length) return <p className="placeholder-row">Er staat nu en straks geen sport op je zenders.</p>;
  return (
    <div className="sh-channels" data-focus-group>
      {channels.map(({ ch, now, next }) => {
        const progress = now ? Math.min(100, Math.max(0, ((Date.now() - now.start) / (now.stop - now.start)) * 100)) : 0;
        return (
          <a key={`${ch.accountId}:${ch.streamId}`} className={`sh-chan${now ? ' is-live' : ''}`} {...linkProps(playLink(ch.accountId, ch.streamId))}>
            <ChannelLogo src={ch.logo} name={ch.name} size={44} />
            <b className="sh-chan-name">{ch.name}</b>
            {now ? <span className="sh-chan-now"><i className="sh-pill is-live">LIVE</i> {now.title}</span> : <span className="subtle">Nu geen sport</span>}
            {now && <span className="sh-chan-bar"><i style={{ width: `${progress}%` }} /></span>}
            {next && <span className="subtle sh-chan-next">{hhmm(next.start)} {next.title}</span>}
          </a>
        );
      })}
    </div>
  );
}

// ---------- Samenvattingen ----------

export function Highlights() {
  const { data, error } = usePolling('hub:highlights', () => hubApi.highlights().then((r) => ({ ...r, poll: 900 })), 900);
  const hide = useHideScores();
  const [playing, setPlaying] = useState<HubVideo | null>(null);
  if (error && !data) return <p className="placeholder-row error">{error}</p>;
  if (!data) return <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />;
  const h = data.hero;
  if (!h) return <p className="placeholder-row">Nog geen samenvattingen gevonden voor wat je volgt. De beheerder kan de YouTube-kanalen aanpassen bij Volgen.</p>;
  const card = (v: HubVideo) => (
    <button key={v.id} type="button" className={`sh-vid${v.fav ? ' is-fav' : ''}`} onClick={() => setPlaying(v)}>
      <span className="sh-vid-art" style={{ backgroundImage: `url("${v.thumb}")` }}>
        {Date.now() - v.published < 36 * 3600_000 && <i className="sh-new">NIEUW</i>}
        {v.match && <b className={`sh-vid-score${hide ? ' is-hidden' : ''}`} data-score>{v.match.home.score}–{v.match.away.score}</b>}
      </span>
      <span className="sh-vid-title">{v.match ? `${v.match.home.short} – ${v.match.away.short}` : v.title}</span>
      <span className="subtle">{v.channel} · {dayText(v.published)}</span>
    </button>
  );
  return (
    <div className={`sh-page${hide ? ' hide-scores' : ''}`}>
      <section className="sh-hl-hero" style={h.match ? { background: `linear-gradient(90deg, var(--c-bg) 35%, transparent), linear-gradient(100deg, #${h.match.home.color ?? '333'} 45%, #${h.match.away.color ?? '222'} 55%)` } : undefined}>
        <span className="sh-hl-eyebrow">{Date.now() - h.published < 36 * 3600_000 ? 'NIEUW · ' : ''}{h.match?.league ?? h.channel} · {dayText(h.published).toUpperCase()}</span>
        <h2>{h.match ? `${h.match.home.short} – ${h.match.away.short}` : h.title}</h2>
        {h.match && (
          <div className="sh-hl-score">
            <span className={hide ? 'is-hidden' : ''} data-score>{h.match.home.score} – {h.match.away.score}</span>
            {!hide && <span className="subtle">{h.match.scorers.map((s) => `${s.minute} ${s.player.split(' ').slice(-1)[0]}`).join(' · ')}</span>}
          </div>
        )}
        <div className="form-actions">
          <button className="btn btn-primary" onClick={() => setPlaying(h)} data-autofocus>▶ Samenvatting</button>
          <a className="btn" href={`https://www.youtube.com/watch?v=${h.id}`} target="_blank" rel="noreferrer">{h.channel} op YouTube</a>
          <button className="btn btn-ghost" onClick={() => void hubApi.saveFollow({ hideScores: !hide }).then(() => setHideScores(!hide))}>{hide ? 'Uitslagen tonen' : 'Uitslagen verbergen'}</button>
        </div>
      </section>
      {data.mine.length > 1 && (
        <section className="row" aria-label="Van jouw clubs"><h2 className="sh-row-title">Van jouw clubs</h2><div className="row-track" data-focus-group>{data.mine.map(card)}</div></section>
      )}
      <section className="row" aria-label="Laatste"><h2 className="sh-row-title">Laatste <span className="subtle">van de competities en sporten die je volgt</span></h2><div className="row-track" data-focus-group>{data.latest.slice(0, 30).map(card)}</div></section>
      {playing && (
        <div className="modal-backdrop" onClick={() => setPlaying(null)}>
          <div className="sh-yt" role="dialog" aria-modal="true" aria-label={playing.title} onClick={(e) => e.stopPropagation()}>
            <iframe title={playing.title} src={`https://www.youtube-nocookie.com/embed/${playing.id}?autoplay=1&rel=0`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
            <div className="form-actions"><span className="subtle">{playing.title}</span><button className="btn" onClick={() => setPlaying(null)} data-autofocus>Sluiten</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Volgen ----------

const TICKER_LABEL: Record<string, string> = {
  'live-league': 'Live: andere wedstrijden in dezelfde competitie',
  'live-clubs': 'Live: clubs die je volgt',
  results: 'Uitslagen van vandaag',
  table: 'Stand van de competitie',
  next: 'Straks op je zenders',
};

export function Follow() {
  const [f, setF] = useState<HubFollow | null>(null);
  const [person, setPerson] = useState('');
  const owner = useIsOwner();
  useEffect(() => {
    hubApi.follow().then(setF).catch(() => setF(null));
  }, []);
  if (!f) return <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />;
  const save = async (patch: Partial<HubFollow>) => {
    setF({ ...f, ...patch });
    const r = await hubApi.saveFollow(patch).catch(() => null);
    if (r) setF((cur) => (cur ? { ...cur, ...r } : cur));
    if (patch.hideScores !== undefined) setHideScores(patch.hideScores);
  };
  const alert = (key: string, field: 'goal' | 'auto', def: boolean) => {
    const cur = f.alerts[key] ?? {};
    const v = cur[field] ?? def;
    return (
      <button type="button" className="chip" aria-pressed={v} onClick={() => void save({ alerts: { ...f.alerts, [key]: { goal: cur.goal ?? true, auto: cur.auto ?? false, [field]: !v } } })}>
        {field === 'goal' ? 'Melding bij doelpunt' : 'Schakel vanzelf over'}
      </button>
    );
  };
  return (
    <div className="sh-follow">
      <section className="settings-block">
        <h2>Meldingen per club</h2>
        <p className="subtle hint">Melding bij een doelpunt (ook als je iets anders kijkt) en 1 minuut voor de aftrap vanzelf naar de wedstrijd. Herinneringen komen 15 minuten van tevoren.</p>
        {f.teams.length === 0 && <p className="subtle">Je volgt nog geen clubs; kies ze hieronder.</p>}
        {f.teams.map((t) => {
          const key = `team:${t.path}|${t.id}`;
          return (
            <div key={key} className="sh-follow-row">
              <Crest logo={t.logo} name={t.short} size={36} /><b>{t.name}</b>
              <span className="qs-chips">{alert(key, 'goal', true)}{alert(key, 'auto', false)}</span>
            </div>
          );
        })}
        <label className="ua2-row"><span className="ua2-row-text"><span>Uitslagen verbergen</span><span className="subtle">Geen standen in de hub en bij samenvattingen, voor als je iets later terugkijkt</span></span>
          <input type="checkbox" className="sw" role="switch" checked={f.hideScores} onChange={(e) => void save({ hideScores: e.target.checked })} />
        </label>
      </section>

      <section className="settings-block">
        <h2>Sport in de Live TV-speler</h2>
        <p className="subtle hint">Speelt een zender een wedstrijd, dan kan de speler er een sportzender van maken. Ook in de speler te wisselen met de knop "Sport".</p>
        <div className="qs-chips">
          {([['off', 'Uit', 'Gewone Live TV-speler'], ['score', 'Alleen stand', 'Stand vast linksboven'], ['full', 'Volledig', 'Alles aan'], ...(f.custom ? [['custom', 'Aangepast', 'Jouw eigen samenstelling hieronder']] : [])] as [HubFollow['skin'], string, string][]).map(([id, label, hint]) => (
            <button key={id} type="button" className="chip" aria-pressed={(f.skin ?? 'full') === id} title={hint} onClick={() => void save({ skin: id })}>{label}</button>
          ))}
        </div>
        {f.parts && (
          <>
            {([['score', 'Stand linksboven', 'De uitslag met de minuut, ook als de bediening weg is'], ['strip', 'Verloopbalk', 'Stand en tijdlijn met doelpunten onderin'], ['ticker', 'Lichtkrant', 'De onderste balk; kies hieronder wat erin komt'], ['panel', 'Wedstrijdcentrum meteen open', 'Alleen op het web: het beeld wordt kleiner, rechts stats en opstelling']] as const).map(([k, label, hint]) => (
              <label key={k} className="ua2-row"><span className="ua2-row-text"><span>{label}</span><span className="subtle">{hint}</span></span>
                <input type="checkbox" className="sw" role="switch" checked={f.parts[k]} onChange={(e) => void save({ parts: { ...f.parts, [k]: e.target.checked } })} />
              </label>
            ))}
            <p className="subtle hint">Zet je hierboven iets los aan of uit, dan wordt dat je eigen weergave "Aangepast". Met de knop Sport in de speler wissel je tussen Uit, Stand, Volledig en Aangepast.</p>
            <h3 className="sh-sub">Lichtkrant</h3>
            <p className="subtle hint">Aan wat je wilt zien, met de pijltjes de volgorde. Een doelpunt elders komt vanzelf vooraan.</p>
            <ol className="sh-ticker-list">
              {[...(f.ticker ?? []), ...(f.tickerChoices ?? []).filter((x) => !(f.ticker ?? []).includes(x))].map((id) => {
                const on = (f.ticker ?? []).includes(id);
                const i = (f.ticker ?? []).indexOf(id);
                const move = (d: number) => { const l = [...f.ticker]; const j = i + d; if (j < 0 || j >= l.length) return; [l[i], l[j]] = [l[j], l[i]]; void save({ ticker: l }); };
                return (
                  <li key={id} className={on ? '' : 'is-off'}>
                    <label><input type="checkbox" checked={on} onChange={() => void save({ ticker: on ? f.ticker.filter((x) => x !== id) : [...f.ticker, id] })} /> {TICKER_LABEL[id] ?? id}</label>
                    {on && <span className="sh-ticker-move"><button type="button" className="icon-mini" aria-label="Omhoog" disabled={i === 0} onClick={() => move(-1)}>▲</button><button type="button" className="icon-mini" aria-label="Omlaag" disabled={i === f.ticker.length - 1} onClick={() => move(1)}>▼</button></span>}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </section>

      <section className="settings-block">
        <h2>Sporten</h2>
        <p className="subtle hint">Wat er van deze sporten op jouw zenders komt, staat bij Voor jou onder "Deze week".</p>
        <div className="qs-chips">
          {f.sportChoices.map((s) => (
            <button key={s.id} type="button" className="chip" aria-pressed={f.sports.includes(s.id)} onClick={() => void save({ sports: f.sports.includes(s.id) ? f.sports.filter((x) => x !== s.id) : [...f.sports, s.id] })}>{s.name}</button>
          ))}
        </div>
      </section>

      <section className="settings-block">
        <h2>Personen</h2>
        <p className="subtle hint">Coureurs, darters, tennissers en vechters: hun wedstrijden in de gids krijgen een ster en staan bovenaan.</p>
        <form className="bk-form" onSubmit={(e) => { e.preventDefault(); if (person.trim().length >= 3) { void save({ people: [...f.people, person.trim()] }); setPerson(''); } }}>
          <input className="text-input" value={person} onChange={(e) => setPerson(e.target.value)} placeholder="bijv. Verstappen, Van Gerwen, Sinner" />
          <button className="btn btn-sm" type="submit" disabled={person.trim().length < 3}>Volgen</button>
        </form>
        <div className="qs-chips">{f.people.map((p) => <button key={p} type="button" className="chip" aria-pressed onClick={() => void save({ people: f.people.filter((x) => x !== p) })}>{p} ✕</button>)}</div>
      </section>

      <section className="settings-block"><h2>Clubs en competities</h2><SportCenterSettings /></section>
      {owner && <YoutubeSources />}
    </div>
  );
}

/** Beheerder: welke officiële YouTube-kanalen voor de samenvattingen. */
function YoutubeSources() {
  const [list, setList] = useState<{ id: string; label: string; leagues: string[] }[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    hubApi.youtube().then((r) => setList(r.sources)).catch(() => setList(null));
  }, []);
  if (!list) return null;
  const set = (i: number, patch: Partial<{ id: string; label: string; leagues: string[] }>) => setList(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <section className="settings-block form-grid">
      <h2>Samenvattingen (YouTube-kanalen)</h2>
      <p className="subtle hint">Alleen jij ziet dit. Per kanaal een handle (@ZiggoSport) of kanaal-id (UC…) en de competities waar het bij hoort (ids zoals eredivisie, ucl, f1, pdc).</p>
      {list.map((x, i) => (
        <div key={i} className="usenet-ix">
          <label>Naam<input className="text-input" value={x.label} onChange={(e) => set(i, { label: e.target.value })} /></label>
          <label>Handle of kanaal-id<input className="text-input" value={x.id} onChange={(e) => set(i, { id: e.target.value.trim() })} /></label>
          <label>Competities<input className="text-input" value={x.leagues.join(', ')} onChange={(e) => set(i, { leagues: e.target.value.split(/[\s,]+/).filter(Boolean) })} /></label>
          <button className="btn btn-ghost btn-sm" onClick={() => setList(list.filter((_, j) => j !== i))}>Verwijderen</button>
        </div>
      ))}
      <div className="form-actions">
        <button className="btn btn-sm" onClick={() => setList([...list, { id: '', label: '', leagues: [] }])}>+ Kanaal</button>
        <button className="btn btn-primary btn-sm" onClick={() => void hubApi.saveYoutube(list).then((r) => { setList(r.sources); setMsg('Opgeslagen.'); }).catch(() => setMsg('Opslaan mislukt.'))}>Opslaan</button>
        <button className="btn btn-ghost btn-sm" onClick={() => void hubApi.saveYoutube(null).then((r) => { setList(r.sources); setMsg('Terug naar de standaardkanalen.'); })}>Standaard</button>
      </div>
      {msg && <p className="subtle" role="status">{msg}</p>}
    </section>
  );
}
